import { Box, makeStyles, Typography } from "@material-ui/core";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import { StatusBadge } from "@/components/Badge";
import { CustomButton } from "@/components/Button";
import {
  ExclusivePageHeading,
  ExclusiveTemplate,
  useExclusiveLayout,
} from "@/components/exclusive/ExclusiveContentLayout";
import { formatLimitedPrice } from "@/components/exclusive/LimitedPropertyCard";
import { LimitedPropertyWithAgent } from "@/components/property/LimitedPropertiesSection";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import theme from "@/theme";
import { AGENT_DETAIL_URL_SOURCE } from "@/utilities/userOffer";
import { calculateYieldRate } from "@/utilities/yield";

import NoImage from "@assets/image/NoImage.svg";

const useStyles = makeStyles({
  columns: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 384fr) minmax(0, 776fr)",
      gap: layout.space[40],
    },
  },
  left: { minWidth: 0 },
  hero: {
    backgroundColor: colors["surface/card"],
    overflow: "hidden",
    [theme.breakpoints.up("md")]: { borderRadius: layout.radius.lg },
  },
  image: {
    display: "block",
    width: "100%",
    height: 240,
    objectFit: "cover",
    backgroundColor: colors["field/bg"],
    [theme.breakpoints.up("md")]: { height: 256 },
  },
  summary: {
    padding: `${layout.space[16]}px ${layout.space[24]}px ${layout.space[24]}px`,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[16],
    [theme.breakpoints.up("md")]: { padding: layout.space[20], gap: layout.space[8] },
  },
  spHead: {
    display: "flex",
    flexDirection: "column",
    gap: layout.space[8],
    alignItems: "flex-start",
    [theme.breakpoints.up("md")]: { display: "none" },
  },
  name: { ...textStyles["SP/Heading/H3"], overflowWrap: "anywhere" },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: layout.space[12],
    [theme.breakpoints.up("md")]: { gridTemplateColumns: "1fr", gap: layout.space[12] },
  },
  metric: {
    display: "flex",
    flexDirection: "column",
    gap: layout.space[4],
    [theme.breakpoints.up("md")]: { flexDirection: "row", justifyContent: "space-between", alignItems: "baseline" },
  },
  number: {
    ...textStyles["Number/XS"],
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: textStyles["Number/S"],
  },
  unit: { ...textStyles["SP/Body/Small"], color: colors["text/secondary"], marginLeft: layout.space[4] },
  details: {
    padding: layout.space[24],
    display: "flex",
    flexDirection: "column",
    gap: layout.space[24],
    minWidth: 0,
    [theme.breakpoints.up("md")]: { padding: 0 },
  },
  section: { display: "flex", flexDirection: "column", gap: layout.space[12] },
  rows: {
    [theme.breakpoints.up("md")]: {
      padding: `${layout.space[8]}px ${layout.space[24]}px`,
      backgroundColor: colors["surface/card"],
      borderRadius: layout.radius.lg,
    },
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: layout.space[16],
    padding: `${layout.space[12]}px 0`,
    borderBottom: `1px solid ${colors["border/default"]}`,
    "&:last-child": { borderBottom: 0 },
  },
  value: {
    ...textStyles["SP/Body/Large"],
    textAlign: "right",
    whiteSpace: "pre-line",
    overflowWrap: "anywhere",
    minWidth: 0,
    maxWidth: "75%",
  },
  inquiries: {
    backgroundColor: colors["surface/card"],
    borderRadius: layout.radius.md,
    padding: `0 ${layout.space[16]}px`,
    [theme.breakpoints.up("md")]: {
      borderRadius: layout.radius.lg,
      padding: `${layout.space[8]}px ${layout.space[24]}px`,
    },
  },
  inquiry: {
    padding: `${layout.space[12]}px 0`,
    borderBottom: `1px solid ${colors["surface/base"]}`,
    "&:last-child": { borderBottom: 0 },
  },
  inquiryHeading: { ...textStyles["SP/Heading/H4"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H3"] },
  inquiryTitle: { ...textStyles["SP/Button/M"], marginBottom: layout.space[4] },
  contact: {
    backgroundColor: colors["surface/trust"],
    color: colors["text/inverse"],
    borderRadius: layout.radius.md,
    padding: layout.space[16],
    display: "flex",
    flexDirection: "column",
    gap: layout.space[12],
    margin: `0 ${layout.space[24]}px ${layout.space[32]}px`,
    [theme.breakpoints.up("md")]: {
      margin: `${layout.space[24]}px 0 0`,
      borderRadius: layout.radius.lg,
      padding: layout.space[24],
    },
  },
  contactTitle: { ...textStyles["SP/Heading/H4"] },
  contactSmall: { ...textStyles["SP/Body/Small"] },
  spContact: { [theme.breakpoints.up("md")]: { display: "none" } },
  pcContact: { display: "none", [theme.breakpoints.up("md")]: { display: "block" } },
  badge: {
    borderRadius: layout.radius.full,
    height: "auto",
    padding: `${layout.space[4]}px ${layout.space[12]}px`,
    ...textStyles["SP/Heading/H5"],
  },
  body: { [theme.breakpoints.down("sm")]: { padding: 0 } },
});
const inquiryItems = [
  ["土地の権利", "所有権か借地権か"],
  ["専有面積と間取り", "図面と合わせて確認できます"],
  ["想定の年間収支", "ローンの条件を入れた試算"],
  ["管理と入居の状況", "いまの入居者と管理会社"],
];
export const LimitedPropertyDetailPage: React.FC = () => {
  const classes = useStyles();
  const common = useExclusiveLayout();
  const history = useHistory();
  const location = useLocation<{ property?: LimitedPropertyWithAgent }>();
  const property = location.state?.property;
  const handleBack = () => history.goBack();
  if (!property)
    return (
      <ExclusiveTemplate headerTitle="限定公開物件" onBack={handleBack} isDense isPcV2 disableBottomPadding>
        <Box className={common.body}>
          <ExclusivePageHeading title="限定公開物件" backLabel="限定公開物件" onBack={handleBack} />
          <Box className={`${common.card} ${common.stack}`}>
            <Typography>物件情報が見つかりませんでした。</Typography>
            <CustomButton customVariant="filled" onClick={() => history.push("/mypage")}>
              マイページへ戻る
            </CustomButton>
          </Box>
        </Box>
      </ExclusiveTemplate>
    );
  const contact = (
    <Box className={classes.contact}>
      <Typography className={classes.contactSmall}>この物件について</Typography>
      <Typography className={classes.contactTitle}>取り扱っているプランナーに聞けます</Typography>
      <Typography className={classes.contactSmall}>資料の送付や内見の相談ができます。</Typography>
      <CustomButton
        buttonSize="M"
        tone="inverse"
        disabled={!property.agent_id}
        onClick={() => {
          if (property.agent_id)
            history.push(`/agent_search/${property.agent_id}?source=${AGENT_DETAIL_URL_SOURCE.LIMITED_PROPERTY}`);
        }}
      >
        このプランナーに問い合わせる
      </CustomButton>
    </Box>
  );
  return (
    <ExclusiveTemplate headerTitle="限定公開物件" onBack={handleBack} isDense isPcV2 disableBottomPadding>
      <Box className={`${common.body} ${classes.body}`}>
        <ExclusivePageHeading
          title={property.property_name || "-"}
          backLabel="限定公開物件"
          onBack={handleBack}
          subtitle={property.property_area || "-"}
          badge={<StatusBadge className={classes.badge} status="info" label="限定公開物件" />}
        />
        <Box className={classes.columns}>
          <Box className={classes.left}>
            <Box className={classes.hero}>
              <img
                className={classes.image}
                src={property.file_url || property.object_storage_path || NoImage}
                alt={property.property_name || "限定公開物件"}
              />
              <Box className={classes.summary}>
                <Box className={classes.spHead}>
                  <StatusBadge className={classes.badge} status="info" label="限定公開物件" />
                  <Typography component="h1" className={classes.name}>
                    {property.property_name || "-"}
                  </Typography>
                  <Typography className={common.text}>{property.property_area || "-"}</Typography>
                </Box>
                <Box className={classes.metrics}>
                  <Box className={classes.metric}>
                    <Typography className={common.small}>販売価格</Typography>
                    <Typography className={classes.number}>
                      {formatLimitedPrice(property.property_price)}
                      {property.property_price != null && <span className={classes.unit}>万円</span>}
                    </Typography>
                  </Box>
                  <Box className={classes.metric}>
                    <Typography className={common.small}>家賃（月）</Typography>
                    <Typography className={classes.number}>
                      {property.property_monthly_rent == null ? "-" : property.property_monthly_rent.toLocaleString()}
                      {property.property_monthly_rent != null && <span className={classes.unit}>円</span>}
                    </Typography>
                  </Box>
                  <Box className={classes.metric}>
                    <Typography className={common.small}>利回り</Typography>
                    <Typography className={classes.number}>
                      {calculateYieldRate(property.property_monthly_rent, property.property_price)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box className={classes.pcContact}>{contact}</Box>
          </Box>
          <Box className={classes.details}>
            <Box className={classes.section}>
              <Typography component="h2" className={common.sectionTitle}>
                物件の情報
              </Typography>
              <Typography className={common.small}>プランナーから提供された内容です。</Typography>
              <Box className={classes.rows}>
                {[
                  ["所在地", property.property_area ?? "-"],
                  ["交通", property.property_nearest_station ?? "-"],
                  ["築年数", property.property_building_age != null ? `築${property.property_building_age}年` : "-"],
                ].map(([label, value]) => (
                  <Box className={classes.row} key={label}>
                    <Typography className={common.small}>{label}</Typography>
                    <Typography className={classes.value}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box className={classes.section}>
              <Typography component="h2" className={classes.inquiryHeading}>
                問い合わせると分かること
              </Typography>
              <Box className={classes.inquiries}>
                {inquiryItems.map(([title, description]) => (
                  <Box key={title} className={classes.inquiry}>
                    <Typography className={classes.inquiryTitle}>{title}</Typography>
                    <Typography className={common.small}>{description}</Typography>
                  </Box>
                ))}
              </Box>
              <Typography className={common.small}>
                この物件は一般に公開していないため、詳しい情報は取り扱っているプランナーからお伝えします。
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box className={classes.spContact}>{contact}</Box>
      </Box>
    </ExclusiveTemplate>
  );
};
